/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * PULSING GRADIENT — Subtle, elegant audio-reactive background
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * A full-canvas gradient that gently shifts color and "breathes" with the music.
 * Very subtle and tasteful — perfect for background use or laid-back sets.
 *
 * HOW IT WORKS:
 * 1. Two color stops form a radial gradient covering the canvas.
 * 2. The hue of each stop slowly drifts based on spectral content:
 *    - Bass-heavy → warm tones (red, orange)
 *    - Treble-heavy → cool tones (blue, purple)
 * 3. The gradient's radius "breathes" — expanding on beats and slowly contracting.
 * 4. Overall brightness responds to loudness.
 *
 * This teaches subtle audio reactivity — not everything needs to be flashy.
 */

import { Visualization, FrameData, VisualizationSetting } from '../../types';
import { BeatPulse, SmoothedValue } from '../../utils/audioUtils';
import { hsl } from '../../utils/colorUtils';

export class PulsingGradient implements Visualization {
  id = 'pulsing-gradient';
  name = 'Pulsing Gradient';
  category = 'minimal' as const;
  description = 'Elegant color-shifting gradient that breathes with the beat';
  renderer = 'canvas2d' as const;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private beatPulse = new BeatPulse(0.96);  // slow decay = gentle breathing
  private smoothHue1 = new SmoothedValue(0.03);
  private smoothHue2 = new SmoothedValue(0.02);
  private smoothBright = new SmoothedValue(0.06);

  private baseHue = 250;   // starting hue (purple-blue)
  private intensity = 1.0;  // overall brightness multiplier

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  render(frame: FrameData): void {
    const { bassEnergy, trebleEnergy, overallEnergy, isBeat, beatIntensity } = frame;
    const { width, height } = this.canvas;
    const ctx = this.ctx;
    const cx = width / 2;
    const cy = height / 2;

    this.beatPulse.update(isBeat, beatIntensity);

    // Hue 1 (center) shifts warm with bass
    const hue1 = this.smoothHue1.update(this.baseHue + bassEnergy * 60 - trebleEnergy * 40);
    // Hue 2 (edge) shifts in opposite direction
    const hue2 = this.smoothHue2.update(this.baseHue + 60 + trebleEnergy * 50 - bassEnergy * 30);
    // Brightness responds to overall energy
    const brightness = this.smoothBright.update(25 + overallEnergy * 30 * this.intensity);

    // Gradient radius "breathes" — expands on beat, contracts slowly
    const maxRadius = Math.max(width, height) * 0.8;
    const breathRadius = maxRadius * (0.7 + this.beatPulse.value * 0.35);

    // Create radial gradient
    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, breathRadius);
    gradient.addColorStop(0, hsl(hue1, 70, brightness + 15));
    gradient.addColorStop(0.5, hsl((hue1 + hue2) / 2, 60, brightness));
    gradient.addColorStop(1, hsl(hue2, 50, brightness - 10));

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Subtle beat flash overlay
    if (this.beatPulse.value > 0.1) {
      ctx.fillStyle = `rgba(255, 255, 255, ${this.beatPulse.value * 0.04})`;
      ctx.fillRect(0, 0, width, height);
    }
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  destroy(): void {}

  getSettings(): VisualizationSetting[] {
    return [
      { key: 'baseHue', label: 'Base Color', type: 'range', value: this.baseHue, min: 0, max: 360, step: 10 },
      { key: 'intensity', label: 'Intensity', type: 'range', value: this.intensity, min: 0.3, max: 2, step: 0.1 },
    ];
  }

  setSettings(settings: Record<string, number | string | boolean>): void {
    if (typeof settings.baseHue === 'number') this.baseHue = settings.baseHue;
    if (typeof settings.intensity === 'number') this.intensity = settings.intensity;
  }
}
